import type { FC, ReactNode } from 'react'
import type { ClaudeSession } from '@/features/events/conversation/state/types'
import type {
  TreeNodeStyleConfig,
  ContentContainerConfig,
  SessionIconRenderer,
  HostIconRenderer,
} from '../types/SessionTreeProps'
import { HostNode } from '../HostNode'
import type { ProjectGroup } from './ProjectNodeRenderer'
import { ProjectNodeRenderer } from './ProjectNodeRenderer'

export interface HostGroup {
  readonly hostname: string
  readonly projects: ProjectGroup[]
  readonly sessions: ClaudeSession[]
}

export interface HostNodeRendererProps {
  readonly host: HostGroup
  readonly hostIcon: ReactNode | HostIconRenderer | null | undefined
  readonly hostStyleConfig: TreeNodeStyleConfig
  readonly projectIcon: ReactNode | null | undefined
  readonly projectStyleConfig: TreeNodeStyleConfig
  readonly sessionIconConfig: ReactNode | SessionIconRenderer | null | undefined
  readonly sessionStyleConfig: TreeNodeStyleConfig
  readonly contentContainerConfig: ContentContainerConfig
  readonly expandPosition: 'left' | 'right'
  readonly showCounts: boolean
  readonly isExpanded: (key: string) => boolean
  readonly onToggle: (key: string) => void
  readonly hostActions?: ReactNode[] | undefined
  readonly hostStats?: ReactNode | undefined
  readonly getProjectActions?:
    | ((projectKey: string, sessions: ClaudeSession[]) => ReactNode[] | undefined)
    | undefined
  readonly getProjectStats?: ((sessions: ClaudeSession[]) => ReactNode | undefined) | undefined
  readonly getSessionActions?: ((session: ClaudeSession) => ReactNode[] | undefined) | undefined
  readonly getSessionPersistentActions?:
    | ((session: ClaudeSession) => ReactNode[] | undefined)
    | undefined
  readonly renderOrchestrator: (session: ClaudeSession, isExpanded: boolean) => ReactNode
}

export const HostNodeRenderer: FC<HostNodeRendererProps> = ({
  host,
  hostIcon,
  hostStyleConfig,
  projectIcon,
  projectStyleConfig,
  sessionIconConfig,
  sessionStyleConfig,
  contentContainerConfig,
  expandPosition,
  showCounts,
  isExpanded,
  onToggle,
  hostActions,
  hostStats,
  getProjectActions,
  getProjectStats,
  getSessionActions,
  getSessionPersistentActions,
  renderOrchestrator,
}) => {
  const resolvedIcon = resolveHostIcon(hostIcon, host.hostname)

  return (
    <HostNode
      hostname={host.hostname}
      label={host.hostname}
      icon={resolvedIcon}
      styleConfig={hostStyleConfig}
      {...(showCounts && { count: host.projects.length })}
      {...(hostActions && { actions: hostActions })}
      {...(hostStats && { stats: hostStats })}
      isExpanded={isExpanded(`host:${host.hostname}`)}
      onToggle={() => onToggle(`host:${host.hostname}`)}
      expandPosition={expandPosition}
    >
      {host.projects.map((project) => (
        <ProjectNodeRenderer
          key={project.projectKey}
          project={project}
          projectIcon={projectIcon}
          projectStyleConfig={projectStyleConfig}
          sessionIconConfig={sessionIconConfig}
          sessionStyleConfig={sessionStyleConfig}
          contentContainerConfig={contentContainerConfig}
          expandPosition={expandPosition}
          showCounts={showCounts}
          isExpanded={isExpanded}
          onToggle={onToggle}
          projectActions={getProjectActions?.(project.projectKey, project.sessions)}
          projectStats={getProjectStats?.(project.sessions)}
          getSessionActions={getSessionActions}
          getSessionPersistentActions={getSessionPersistentActions}
          renderOrchestrator={renderOrchestrator}
        />
      ))}
    </HostNode>
  )
}

function resolveHostIcon(
  iconConfig: ReactNode | HostIconRenderer | null | undefined,
  hostname: string,
): ReactNode | null {
  if (iconConfig === null || iconConfig === undefined) {
    return null
  }
  if (typeof iconConfig === 'function') {
    return iconConfig(hostname)
  }
  return iconConfig
}
